<template>
  <Slideover :visible="visible">
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="$emit('toggle-slideover')"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <!-- Main -->
    <div class="flex flex-col py-3 px-2 mx-4">
      <table>
        <thead>
          <tr>
            <th class="py-2">SKU</th>
            <th class="py-2">Qty Ordered</th>
            <th class="py-2">Qty Committed</th>
            <th class="py-2">Qty Needed</th>
            <th class="py-2">QOH</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="detail in selectable" :key="detail.ItemNumber">
            <td class="py-1">
              <BaseCheckbox
                v-model="detail.selected"
                :disabled="!detail.canBeFulfilled"
              >
                {{ detail.SKU }}
                <span
                  v-if="!loadingFulfillmentStatus && !detail.canBeFulfilled"
                  class="text-red-500"
                >
                  (No QOH)
                </span>
              </BaseCheckbox>
            </td>
            <td class="py-2 text-center">{{ detail.QuantityOrdered }}</td>
            <td class="py-2 text-center">{{ detail.QuantityShipped }}</td>
            <td class="py-2 text-center">{{ detail.QuantityNeeded }}</td>
            <td class="py-2 text-center">{{ detail.inventory.QOH }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="loadingFulfillmentStatus"
      class="w-full flex justify-center space-x-2 text-gray-600"
    >
      <LoadingSpinner />
      <span>Loading items fulfillment status</span>
    </div>

    <HttpError :error="error" />
    <FulfillmentError :in-fulfillment="inFulfillment" action="Fill BO" />

    <!-- Footer -->
    <div class="flex justify-end px-4 py-4 space-x-4">
      <span class="inline-flex rounded-md shadow-sm">
        <button
          type="button"
          @click="$emit('toggle-slideover')"
          class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
        >
          Cancel
        </button>
      </span>
      <span class="inline-flex rounded-md shadow-sm">
        <Button
          @click-handler="process"
          :disabled="!canProcess || processing"
          class="flex items-center space-x-2"
        >
          <span>Fill Backorder</span>
          <LoadingSpinner v-if="processing" class="text-white" />
        </Button>
      </span>
    </div>
  </Slideover>
</template>
<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Button from "@/components/elements/Button.vue";
import Slideover from "@/components/layout/Slideover.vue";
import FulfillmentError from "@/components/elements/FulfillmentError";

const Order = ApiFactory.get("orders");
const Inventory = ApiFactory.get("inventories");

export default {
  name: "FillBackorder",
  components: {
    FulfillmentError,
    Button,
    Slideover,
  },
  props: {
    title: {
      type: String,
      default: "Title",
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectable: [],
      error: null,
      processing: false,
      loadingFulfillmentStatus: false,
    };
  },
  computed: {
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    details() {
      return this.order.details;
    },
    fulfillable() {
      return this.details.filter((detail) => {
        return (
          this.detailIsNotAdjustment(detail) &&
          this.detailHasQuantityNeeded(detail) &&
          this.detailIsNotDropShip(detail) &&
          this.detailDoesNotHaveParent(detail) &&
          this.detailHasNotBeenShipped(detail)
        );
      });
    },
    orderStatus() {
      return this.order.status.Status;
    },
    inFulfillment() {
      return this.orderStatus === "In Fulfillment";
    },
    selected() {
      return this.selectable.filter((detail) => detail.selected);
    },
    canProcess() {
      return !this.inFulfillment && this.selected.length;
    },
  },
  mounted() {
    this.selectable = this.getSelectableItems();
    this.loadFulfillmentStatus();
  },
  methods: {
    getSelectableItems() {
      return this.fulfillable.map((detail) => {
        return {
          canBeFulfilled: false,
          selected: false,
          ...detail,
        };
      });
    },
    loadFulfillmentStatus() {
      this.loadingFulfillmentStatus = true;

      Inventory.fulfillableStatus(
        this.selectable.reduce((request, detail) => {
          request[detail.SKU] = detail.QuantityNeeded;
          return request;
        }, {})
      ).then((response) => this.updateFulfillmentStatus(response.data.body));
    },
    updateFulfillmentStatus(status) {
      for (let detail of this.selectable) {
        detail.canBeFulfilled =
          status.find((item) => item.LocalSKU === detail.SKU)
            ?.FulfillmentStatus || false;
      }

      this.loadingFulfillmentStatus = false;
    },
    detailIsNotAdjustment(detail) {
      return detail.Adjustment == 0;
    },
    detailHasQuantityNeeded(detail) {
      return detail.QuantityNeeded > 0;
    },
    detailIsNotDropShip(detail) {
      return detail.DropShip == 0;
    },
    detailDoesNotHaveParent(detail) {
      return [null, 0, "0"].indexOf(detail.Parent) >= 0;
    },
    detailHasNotBeenShipped(detail) {
      return [null, ""].indexOf(detail.DateShipped) >= 0;
    },
    process() {
      this.processing = true;
      Order.fillBackorders(this.order.SourceOrderNumber, {
        items: this.selected.map((detail) => detail.ItemNumber),
      })
        .then(() => this.handleSuccess())
        .catch((error) => (this.error = error))
        .finally(() => (this.processing = false));
    },
    handleSuccess() {
      this.$emit("toggle-slideover");
      this.$emit("refresh-order");
    },
  },
};
</script>
